import { GameConfig } from "@/config/GameConfig";
import type { LevelDefinition } from "./types";

// Same 18x18 board as world1/world2/world3 - SATURN's identity is
// "Orbital Lanes + Ring Stations": rings, parallel lanes, and (from
// w4-l2 onward) permanent authored map portals as an optional
// route-planning tool - never a maze, never mandatory, never the sole
// path into any region. Every obstacle coordinate below is the exact,
// locked Saturn design-pass geometry (re-verified cell-by-cell against
// the canonical 18x18 ASCII grids in the PR description, including the
// post-launch gameplay-QA pass that added the outer perimeter rings to
// w4-l1/w4-l2, the small inner ring to w4-l2, the BLACK HOLE hazard to
// w4-l3, and retuned w4-l1/w4-l2/w4-l4's start direction and w4-l3/w4-l5's
// target length) - not a re-derivation via the `rect`/`wall` authoring
// helpers, so there is zero risk of a helper-composition drifting from
// the locked spec. A second gameplay-QA pass then turned w4-l3's single
// fixed portal pair into a 3-endpoint random `PortalNetwork` and
// re-paired w4-l4's two fixed pairs into a large diagonal "X" across the
// arena; a THIRD pass re-spread w4-l3's three network endpoints far
// apart (they were too close together, risking an unfair Model A
// self-collision on a random exit) - see each level's own comment below.
const GRID = { cols: 18, rows: 18 };

export const WORLD_4_LEVELS: LevelDefinition[] = [
  {
    id: "w4-l1",
    name: "RING ROAD",
    worldId: "w4",
    levelInWorld: 1,
    order: 16,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    // Gameplay-QA fix: the original "right" heading pushed the player
    // straight toward the main ring's own left wall (4,9) almost
    // immediately. "up" keeps the same locked start cell and gives a long,
    // genuinely open runway up the west margin before any wall matters.
    startDirection: "up",
    initialLength: 3,
    targetLength: 34,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w4,
    // A single broken ring with three doors (N/S/E) - Saturn's own
    // introduction to ring/lane language. The outer lane (around the
    // whole ring via the N or S margin) and the inner lane (through the
    // N or S door, across the interior, out the E door) are two genuinely
    // different routes to the same exit. No portal - that mechanic is
    // introduced on its own in w4-l2.
    //
    // Gameplay-QA pass: added a complete outer perimeter ring on the
    // literal board boundary (x=0, x=17, y=0, y=17). This is a second,
    // larger ring around the first - a stronger "orbital" identity - and
    // it deliberately shrinks the playable arena by exactly one layer:
    // the head can never legally stand on a boundary cell anymore, so a
    // WORMHOLE wrap (which only ever triggers from a raw out-of-bounds
    // move starting AT the literal edge) can never be initiated on this
    // level in practice. WORMHOLE itself is unchanged; it's simply
    // functionally unreachable here - see the PR description's own note
    // on this tradeoff. The new ring doesn't overlap `snakeStart`/`exit`
    // (both are already inset from the literal edge) and doesn't wall off
    // anything that wasn't already open margin, so connectivity, the
    // outer-vs-inner route choice, and "zero true dead ends" all hold
    // exactly as before.
    obstacles: [
      { x: 0, y: 0 }, { x: 1, y: 0 }, { x: 2, y: 0 }, { x: 3, y: 0 }, { x: 4, y: 0 }, { x: 5, y: 0 }, { x: 6, y: 0 }, { x: 7, y: 0 }, { x: 8, y: 0 }, { x: 9, y: 0 }, { x: 10, y: 0 }, { x: 11, y: 0 }, { x: 12, y: 0 }, { x: 13, y: 0 }, { x: 14, y: 0 }, { x: 15, y: 0 }, { x: 16, y: 0 }, { x: 17, y: 0 },
      { x: 0, y: 1 }, { x: 17, y: 1 },
      { x: 0, y: 2 }, { x: 17, y: 2 },
      { x: 0, y: 3 }, { x: 17, y: 3 },
      { x: 0, y: 4 }, { x: 4, y: 4 }, { x: 5, y: 4 }, { x: 6, y: 4 }, { x: 7, y: 4 }, { x: 10, y: 4 }, { x: 11, y: 4 }, { x: 12, y: 4 }, { x: 13, y: 4 }, { x: 17, y: 4 },
      { x: 0, y: 5 }, { x: 4, y: 5 }, { x: 13, y: 5 }, { x: 17, y: 5 },
      { x: 0, y: 6 }, { x: 4, y: 6 }, { x: 13, y: 6 }, { x: 17, y: 6 },
      { x: 0, y: 7 }, { x: 4, y: 7 }, { x: 13, y: 7 }, { x: 17, y: 7 },
      { x: 0, y: 8 }, { x: 4, y: 8 }, { x: 17, y: 8 },
      { x: 0, y: 9 }, { x: 4, y: 9 }, { x: 17, y: 9 },
      { x: 0, y: 10 }, { x: 4, y: 10 }, { x: 13, y: 10 }, { x: 17, y: 10 },
      { x: 0, y: 11 }, { x: 4, y: 11 }, { x: 13, y: 11 }, { x: 17, y: 11 },
      { x: 0, y: 12 }, { x: 4, y: 12 }, { x: 13, y: 12 }, { x: 17, y: 12 },
      { x: 0, y: 13 }, { x: 4, y: 13 }, { x: 5, y: 13 }, { x: 6, y: 13 }, { x: 7, y: 13 }, { x: 10, y: 13 }, { x: 11, y: 13 }, { x: 12, y: 13 }, { x: 13, y: 13 }, { x: 17, y: 13 },
      { x: 0, y: 14 }, { x: 17, y: 14 },
      { x: 0, y: 15 }, { x: 17, y: 15 },
      { x: 0, y: 16 }, { x: 17, y: 16 },
      { x: 0, y: 17 }, { x: 1, y: 17 }, { x: 2, y: 17 }, { x: 3, y: 17 }, { x: 4, y: 17 }, { x: 5, y: 17 }, { x: 6, y: 17 }, { x: 7, y: 17 }, { x: 8, y: 17 }, { x: 9, y: 17 }, { x: 10, y: 17 }, { x: 11, y: 17 }, { x: 12, y: 17 }, { x: 13, y: 17 }, { x: 14, y: 17 }, { x: 15, y: 17 }, { x: 16, y: 17 }, { x: 17, y: 17 },
    ],
    exit: { x: 16, y: 9 },
    pace: { paceParMs: 207000, pacePointsPerSecond: 2, maxPaceBonus: 102 },
  },
  {
    id: "w4-l2",
    name: "THE GATE RING",
    worldId: "w4",
    levelInWorld: 2,
    order: 17,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    // Same gameplay-QA start-direction fix as w4-l1 - see that level's
    // own comment.
    startDirection: "up",
    initialLength: 3,
    targetLength: 35,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w4,
    // The same ring vocabulary as RING ROAD, now a plain 2-door ring (no
    // inner-lane shortcut this time - the lesson here is the portal, not
    // lane-switching). One portal pair (see `portals` below) gives a
    // clearly, satisfyingly faster route than the ring itself, while the
    // ring route remains fully legal and not punishing - Saturn's first,
    // safest possible portal introduction.
    //
    // Gameplay-QA pass: added the same outer perimeter ring as RING ROAD
    // (see that level's comment - same WORMHOLE-access consequence, same
    // connectivity/route-choice guarantees), plus one small inner ring -
    // `rectBorder(7,7,10,10)` with N/S doors at (8-9,7)/(8-9,10) - deep
    // inside the main ring's own interior. It's a true 2-door through-room
    // (never a sealed box), its 2x2 core is reachable from either door,
    // it never touches or narrows either portal endpoint's own 4/4-free-
    // neighbor invariant (both stay 4/4 - see `world4.balance.test.ts`),
    // and it's purely textural: the portal (still `p1`, still
    // `(2,2)<->(14,15)`, untouched) remains exactly as optional and exactly
    // as satisfying a shortcut as before.
    obstacles: [
      { x: 0, y: 0 }, { x: 1, y: 0 }, { x: 2, y: 0 }, { x: 3, y: 0 }, { x: 4, y: 0 }, { x: 5, y: 0 }, { x: 6, y: 0 }, { x: 7, y: 0 }, { x: 8, y: 0 }, { x: 9, y: 0 }, { x: 10, y: 0 }, { x: 11, y: 0 }, { x: 12, y: 0 }, { x: 13, y: 0 }, { x: 14, y: 0 }, { x: 15, y: 0 }, { x: 16, y: 0 }, { x: 17, y: 0 },
      { x: 0, y: 1 }, { x: 17, y: 1 },
      { x: 0, y: 2 }, { x: 17, y: 2 },
      { x: 0, y: 3 }, { x: 17, y: 3 },
      { x: 0, y: 4 }, { x: 4, y: 4 }, { x: 5, y: 4 }, { x: 6, y: 4 }, { x: 7, y: 4 }, { x: 10, y: 4 }, { x: 11, y: 4 }, { x: 12, y: 4 }, { x: 13, y: 4 }, { x: 17, y: 4 },
      { x: 0, y: 5 }, { x: 4, y: 5 }, { x: 13, y: 5 }, { x: 17, y: 5 },
      { x: 0, y: 6 }, { x: 4, y: 6 }, { x: 13, y: 6 }, { x: 17, y: 6 },
      { x: 0, y: 7 }, { x: 4, y: 7 }, { x: 7, y: 7 }, { x: 10, y: 7 }, { x: 13, y: 7 }, { x: 17, y: 7 },
      { x: 0, y: 8 }, { x: 4, y: 8 }, { x: 7, y: 8 }, { x: 10, y: 8 }, { x: 13, y: 8 }, { x: 17, y: 8 },
      { x: 0, y: 9 }, { x: 4, y: 9 }, { x: 7, y: 9 }, { x: 10, y: 9 }, { x: 13, y: 9 }, { x: 17, y: 9 },
      { x: 0, y: 10 }, { x: 4, y: 10 }, { x: 7, y: 10 }, { x: 10, y: 10 }, { x: 13, y: 10 }, { x: 17, y: 10 },
      { x: 0, y: 11 }, { x: 4, y: 11 }, { x: 13, y: 11 }, { x: 17, y: 11 },
      { x: 0, y: 12 }, { x: 4, y: 12 }, { x: 13, y: 12 }, { x: 17, y: 12 },
      { x: 0, y: 13 }, { x: 4, y: 13 }, { x: 5, y: 13 }, { x: 6, y: 13 }, { x: 7, y: 13 }, { x: 10, y: 13 }, { x: 11, y: 13 }, { x: 12, y: 13 }, { x: 13, y: 13 }, { x: 17, y: 13 },
      { x: 0, y: 14 }, { x: 17, y: 14 },
      { x: 0, y: 15 }, { x: 17, y: 15 },
      { x: 0, y: 16 }, { x: 17, y: 16 },
      { x: 0, y: 17 }, { x: 1, y: 17 }, { x: 2, y: 17 }, { x: 3, y: 17 }, { x: 4, y: 17 }, { x: 5, y: 17 }, { x: 6, y: 17 }, { x: 7, y: 17 }, { x: 8, y: 17 }, { x: 9, y: 17 }, { x: 10, y: 17 }, { x: 11, y: 17 }, { x: 12, y: 17 }, { x: 13, y: 17 }, { x: 14, y: 17 }, { x: 15, y: 17 }, { x: 16, y: 17 }, { x: 17, y: 17 },
    ],
    exit: { x: 16, y: 9 },
    portals: [{ id: "p1", a: { x: 2, y: 2 }, b: { x: 14, y: 15 } }],
    pace: { paceParMs: 215000, pacePointsPerSecond: 2, maxPaceBonus: 105 },
  },
  {
    id: "w4-l3",
    name: "JUNCTION",
    worldId: "w4",
    levelInWorld: 3,
    order: 18,
    grid: GRID,
    snakeStart: { x: 3, y: 13 },
    startDirection: "right",
    initialLength: 3,
    // Gameplay-QA pass: 36 -> 35. Geometry/portal unchanged - net growth
    // 32, same as every other balance number on this level.
    targetLength: 35,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w4,
    // A west lane and an east lane split by a median wall, connected at
    // the top junction, the bottom junction, and a mid-board crossover
    // gap - three structurally different ways across.
    //
    // Gameplay-QA pass: the single fixed portal pair became one 3-endpoint
    // RANDOM network (see `portals` below) - entering any one of the three
    // resolves to a uniformly random OTHER endpoint, never back to the one
    // just entered.
    //
    // Second gameplay-QA pass: real manual play surfaced a Model A body
    // hazard - the first version clustered all three endpoints on the
    // mid-board crossover band, and two of them ((3,8) and (5,9)) were
    // only 3 cells apart, so a random exit could land right next to the
    // still-present pre-portal body and read as an unfair self-collision.
    // Re-spread across three genuinely distinct, distant regions of the
    // arena instead: (3,4) west lane/upper, (14,11) east lane/lower,
    // (5,15) west lane/south - every pairwise Manhattan distance is now
    // 13-18 cells (see `world4.balance.test.ts`'s locked-distance
    // regression test), far above the ~8-cell target, making it much
    // harder for a still-trailing body segment near one region to also
    // reach a different, distant region. All three are still 4/4-free
    // with generous multi-cell runway in every direction (never placed on
    // the 1-cell-runway top/bottom edge rows), well clear of the black
    // hole (8,8), the exit, and the starting snake's forced first moves
    // (row y=13) - see `world4.balance.test.ts` for the exact invariant
    // checks. None of the three touch the top-junction/mid-gap/bottom-
    // junction route cells, so all three original routes stay exactly as
    // open as before, and the network remains a pure optional wildcard.
    obstacles: [
      { x: 8, y: 2 }, { x: 9, y: 2 },
      { x: 8, y: 3 }, { x: 9, y: 3 },
      { x: 8, y: 4 }, { x: 9, y: 4 },
      { x: 8, y: 5 }, { x: 9, y: 5 },
      { x: 8, y: 6 }, { x: 9, y: 6 },
      { x: 8, y: 7 }, { x: 9, y: 7 },
      { x: 8, y: 10 }, { x: 9, y: 10 },
      { x: 8, y: 11 }, { x: 9, y: 11 },
      { x: 8, y: 12 }, { x: 9, y: 12 },
      { x: 8, y: 13 }, { x: 9, y: 13 },
      { x: 8, y: 14 }, { x: 9, y: 14 },
      { x: 8, y: 15 }, { x: 9, y: 15 },
    ],
    exit: { x: 16, y: 13 },
    portals: [
      {
        id: "p1",
        mode: "random-network",
        endpoints: [
          { x: 3, y: 4 },
          { x: 14, y: 11 },
          { x: 5, y: 15 },
        ],
      },
    ],
    // BLACK HOLE V1: one authored hazard at (8,8), inside the mid-board
    // crossover gap (the 2x2 free block x in {8,9} * y in {8,9} - the
    // median wall's own gap, never an obstacle cell). It sits on ONE of
    // the 4 crossing cells, not all of them: (9,8)/(8,9)/(9,9) stay free,
    // so the mid-gap route remains fully crossable, just no longer a
    // completely safe default - a hazard the player can consciously
    // route around, not an unavoidable death. It's far from the
    // compulsory first 1-2 moves (start is at (3,13), ten rows away),
    // far from the portal's own destination (14,4), and doesn't touch
    // the top-junction or bottom-junction routes at all - all three
    // original route choices remain fully intact.
    blackHoles: [{ x: 8, y: 8 }],
    pace: { paceParMs: 216000, pacePointsPerSecond: 2, maxPaceBonus: 96 },
  },
  {
    id: "w4-l4",
    name: "DOUBLE ORBIT",
    worldId: "w4",
    levelInWorld: 4,
    order: 19,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    // Gameplay-QA fix: geometry/portals/target/stars/pace/exit are all
    // approved as-is - only the start heading changes, same reasoning as
    // w4-l1/w4-l2 (the original "right" heading drove straight at the
    // nearby outer-ring wall almost immediately; "up" runs along the
    // already-open west margin, through the ring's own west door far
    // later, with a long safe runway first).
    startDirection: "up",
    initialLength: 3,
    targetLength: 37,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w4,
    // A 4-door outer rotary ring around a nested 2-door inner "station"
    // ring, plus two portal pairs - both still land in the generous
    // 2-wide annulus band between the two rings, never inside the tiny
    // inner core, so landing is always predictably fair for a long snake.
    //
    // Gameplay-QA pass: re-paired/repositioned (geometry/target/start/
    // exit/stars/pace all untouched) so the two pairs read as a large
    // diagonal "X" across the whole arena instead of two short, local
    // jumps - p1 keeps its existing (1,1) outer-rotary corner (top-left)
    // but now lands at (12,12), deep in the annulus near the BOTTOM-RIGHT
    // of the inner ring; p2 keeps its existing (16,1) corner (top-right)
    // and now lands at (5,12), near the BOTTOM-LEFT of the inner ring -
    // the mirror-image jump. Both new endpoints are 4/4-free, well clear
    // of the inner core, the exit, and the starting body - see
    // `world4.balance.test.ts`.
    obstacles: [
      { x: 3, y: 3 }, { x: 4, y: 3 }, { x: 5, y: 3 }, { x: 6, y: 3 }, { x: 7, y: 3 }, { x: 10, y: 3 }, { x: 11, y: 3 }, { x: 12, y: 3 }, { x: 13, y: 3 }, { x: 14, y: 3 },
      { x: 3, y: 4 }, { x: 14, y: 4 },
      { x: 3, y: 5 }, { x: 14, y: 5 },
      { x: 3, y: 6 }, { x: 6, y: 6 }, { x: 7, y: 6 }, { x: 10, y: 6 }, { x: 11, y: 6 }, { x: 14, y: 6 },
      { x: 3, y: 7 }, { x: 6, y: 7 }, { x: 11, y: 7 }, { x: 14, y: 7 },
      { x: 6, y: 8 }, { x: 11, y: 8 },
      { x: 6, y: 9 }, { x: 11, y: 9 },
      { x: 3, y: 10 }, { x: 6, y: 10 }, { x: 11, y: 10 }, { x: 14, y: 10 },
      { x: 3, y: 11 }, { x: 6, y: 11 }, { x: 7, y: 11 }, { x: 10, y: 11 }, { x: 11, y: 11 }, { x: 14, y: 11 },
      { x: 3, y: 12 }, { x: 14, y: 12 },
      { x: 3, y: 13 }, { x: 14, y: 13 },
      { x: 3, y: 14 }, { x: 4, y: 14 }, { x: 5, y: 14 }, { x: 6, y: 14 }, { x: 7, y: 14 }, { x: 10, y: 14 }, { x: 11, y: 14 }, { x: 12, y: 14 }, { x: 13, y: 14 }, { x: 14, y: 14 },
    ],
    exit: { x: 16, y: 9 },
    portals: [
      { id: "p1", a: { x: 1, y: 1 }, b: { x: 12, y: 12 } },
      { id: "p2", a: { x: 16, y: 1 }, b: { x: 5, y: 12 } },
    ],
    pace: { paceParMs: 239000, pacePointsPerSecond: 2, maxPaceBonus: 114 },
  },
  {
    id: "w4-l5",
    name: "THE RELAY",
    worldId: "w4",
    levelInWorld: 5,
    order: 20,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    startDirection: "right",
    initialLength: 3,
    // Gameplay-QA pass: 38 -> 32. Geometry/portals/base speed/Campaign
    // cap/start/exit are all unchanged - the level was extremely
    // difficult in real play at target 38; this keeps its very hard
    // routing challenge (still Saturn's densest, most complex map) while
    // no longer also demanding excessive body length on top of it.
    targetLength: 32,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w4,
    // World finale: the same outer-rotary/inner-ring vocabulary as
    // DOUBLE ORBIT, plus four isolated 2x2 corner "boulder" clusters for
    // finale texture (no connectivity effect - each is well clear of
    // every corridor) and two asymmetric portal pairs, both landing in
    // the generous annulus band, never the inner core - highest density
    // and route complexity of the world, still fully readable, still
    // leaving clear headroom for Sun.
    obstacles: [
      { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 15, y: 1 }, { x: 16, y: 1 },
      { x: 1, y: 2 }, { x: 2, y: 2 }, { x: 15, y: 2 }, { x: 16, y: 2 },
      { x: 3, y: 3 }, { x: 4, y: 3 }, { x: 5, y: 3 }, { x: 6, y: 3 }, { x: 7, y: 3 }, { x: 10, y: 3 }, { x: 11, y: 3 }, { x: 12, y: 3 }, { x: 13, y: 3 }, { x: 14, y: 3 },
      { x: 3, y: 4 }, { x: 14, y: 4 },
      { x: 3, y: 5 }, { x: 14, y: 5 },
      { x: 3, y: 6 }, { x: 6, y: 6 }, { x: 7, y: 6 }, { x: 10, y: 6 }, { x: 11, y: 6 }, { x: 14, y: 6 },
      { x: 3, y: 7 }, { x: 6, y: 7 }, { x: 11, y: 7 }, { x: 14, y: 7 },
      { x: 6, y: 8 }, { x: 11, y: 8 },
      { x: 6, y: 9 }, { x: 11, y: 9 },
      { x: 3, y: 10 }, { x: 6, y: 10 }, { x: 11, y: 10 }, { x: 14, y: 10 },
      { x: 3, y: 11 }, { x: 6, y: 11 }, { x: 7, y: 11 }, { x: 10, y: 11 }, { x: 11, y: 11 }, { x: 14, y: 11 },
      { x: 3, y: 12 }, { x: 14, y: 12 },
      { x: 3, y: 13 }, { x: 14, y: 13 },
      { x: 3, y: 14 }, { x: 4, y: 14 }, { x: 5, y: 14 }, { x: 6, y: 14 }, { x: 7, y: 14 }, { x: 10, y: 14 }, { x: 11, y: 14 }, { x: 12, y: 14 }, { x: 13, y: 14 }, { x: 14, y: 14 },
      { x: 1, y: 15 }, { x: 2, y: 15 }, { x: 15, y: 15 }, { x: 16, y: 15 },
      { x: 1, y: 16 }, { x: 2, y: 16 }, { x: 15, y: 16 }, { x: 16, y: 16 },
    ],
    exit: { x: 16, y: 9 },
    portals: [
      { id: "p1", a: { x: 1, y: 6 }, b: { x: 12, y: 5 } },
      { id: "p2", a: { x: 9, y: 16 }, b: { x: 5, y: 12 } },
    ],
    pace: { paceParMs: 212000, pacePointsPerSecond: 2, maxPaceBonus: 87 },
  },
];
